import axios from "axios";
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import apiUrls from "../../config/api-urls";
import tokenService from "../../services/token-service";
import { fetchUserApplications } from "../../utils/application-service";

export default function UploadDocumentsPage() {
  const [cv, setCv] = useState(null);
  const [cnicFront, setCnicFront] = useState(null);
  const [cnicBack, setCnicBack] = useState(null);
  const [other, setOther] = useState(null);

  const cvInputRef = useRef(null);
  const cnicFrontInputRef = useRef(null);
  const cnicBackInputRef = useRef(null);
  const otherInputRef = useRef(null);

  const [jobApplicationId, setJobApplicationId] = useState("");
  const [loading, setLoading] = useState(false);
  const [totalSelectedBytes, setTotalSelectedBytes] = useState(0);
  const [cnicFrontPreviewUrl, setCnicFrontPreviewUrl] = useState("");
  const [cnicBackPreviewUrl, setCnicBackPreviewUrl] = useState("");
  const [documentStatus, setDocumentStatus] = useState({
    cv: "Pending",
    cnicFront: "Pending",
    cnicBack: "Pending",
  });
  const userDetails = tokenService.getUserDetails();
  const token = tokenService.getToken();
  const navigate = useNavigate();

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const response = await fetchUserApplications(userDetails?._id, token);
        if (response?.applications?.length) {
           const activeApplication = response.applications.find(app => !app.isArchived);
           const targetApplication = activeApplication || response.applications[0];
           const status = targetApplication.documentStatus || {};
           
          setDocumentStatus({
            cv: status.cv || "Pending",
            cnicFront: status.cnicFront || "Pending",
            cnicBack: status.cnicBack || "Pending",
          });
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    if (token && userDetails?._id) {
      loadData();
    }
  }, [token, userDetails?._id]);

  useEffect(() => {
    const token = tokenService.getToken();
    const fetchLatestResult = async () => {
      try {
        setLoading(true);
        const response = await fetch(apiUrls.tests.result, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        if (!response.ok) {
          throw new Error("Failed to fetch test results");
        }
        const data = await response.json();
        const jobApplicationId = data?.results[0].jobApplicationId;
        if (jobApplicationId) {
          setJobApplicationId(jobApplicationId);
          localStorage.setItem("jobApplicationId", jobApplicationId);
          return;
        }
        throw new Error("No application ID found in test results");
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    fetchLatestResult();
  }, []);

  const isApproved = (status) => status?.toLowerCase() === "approved";
  const isRejected = (status) => status?.toLowerCase() === "rejected";
  const isPending = (status) => status?.toLowerCase() === "pending";

  const allApproved =
    isApproved(documentStatus.cv) &&
    isApproved(documentStatus.cnicFront) &&
    isApproved(documentStatus.cnicBack);

  const canUpload =
    isPending(documentStatus.cv) ||
    isPending(documentStatus.cnicFront) ||
    isPending(documentStatus.cnicBack) ||
    isRejected(documentStatus.cv) ||
    isRejected(documentStatus.cnicFront) ||
    isRejected(documentStatus.cnicBack);

  const recomputeTotalBytes = (next = {}) => {
    const nextCv = Object.prototype.hasOwnProperty.call(next, "cv")
      ? next.cv
      : cv;
    const nextCnicFront = Object.prototype.hasOwnProperty.call(
      next,
      "cnicFront"
    )
      ? next.cnicFront
      : cnicFront;
    const nextCnicBack = Object.prototype.hasOwnProperty.call(next, "cnicBack")
      ? next.cnicBack
      : cnicBack;
    const nextOther = Object.prototype.hasOwnProperty.call(next, "other")
      ? next.other
      : other;
    const sum = [nextCv, nextCnicFront, nextCnicBack, nextOther]
      .filter(Boolean)
      .reduce((acc, f) => acc + (f?.size || 0), 0);
    setTotalSelectedBytes(sum);
  };

  const handleFileChange = (setter, key) => (e) => {
    const file = e.target.files && e.target.files[0];

    if (file && (key === "cnicFront" || key === "cnicBack")) {
      const allowedImageTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/bmp", "image/gif"];
      const fileExt = file.name.split('.').pop().toLowerCase();
      const allowedExts = ["jpg", "jpeg", "png", "webp", "bmp", "gif"];
      if (!allowedImageTypes.includes(file.type) && !allowedExts.includes(fileExt)) {
        Swal.fire({
          icon: "warning",
          title: "Invalid File Format",
          text: "CNIC must be an image file (JPG, JPEG, PNG, WEBP, etc.).",
          confirmButtonText: "OK",
        });
        e.target.value = "";
        setter(null);
        recomputeTotalBytes({ [key]: null });
        if (key === "cnicFront") {
          if (cnicFrontPreviewUrl) URL.revokeObjectURL(cnicFrontPreviewUrl);
          setCnicFrontPreviewUrl("");
        }
        if (key === "cnicBack") {
          if (cnicBackPreviewUrl) URL.revokeObjectURL(cnicBackPreviewUrl);
          setCnicBackPreviewUrl("");
        }
        return;
      }
    }

    setter(file || null);
    recomputeTotalBytes({ [key]: file || null });

    // Update previews immediately for CNIC images
    if (key === "cnicFront") {
      if (cnicFrontPreviewUrl) URL.revokeObjectURL(cnicFrontPreviewUrl);
      const isImg =
        !!file &&
        typeof file.type === "string" &&
        file.type.startsWith("image/");
      setCnicFrontPreviewUrl(isImg ? URL.createObjectURL(file) : "");
    }
    if (key === "cnicBack") {
      if (cnicBackPreviewUrl) URL.revokeObjectURL(cnicBackPreviewUrl);
      const isImg =
        !!file &&
        typeof file.type === "string" &&
        file.type.startsWith("image/");
      setCnicBackPreviewUrl(isImg ? URL.createObjectURL(file) : "");
    }
  };

  const validate = () => {
    if (!jobApplicationId) {
      Swal.fire({
        icon: "error",
        title: "Application Not Found",
        text: "Unable to find your application. Please complete the testing process first.",
        confirmButtonText: "OK",
      });
      return false;
    }

    if (!cv && !isApproved(documentStatus.cv)) {
      Swal.fire({
        icon: "warning",
        title: "Missing CV",
        text: "Please attach your CV.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
      return false;
    }
    if (!cnicFront && !isApproved(documentStatus.cnicFront)) {
      Swal.fire({
        icon: "warning",
        title: "Missing CNIC Front",
        text: "Please attach the front side of your CNIC.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
      return false;
    }
    if (!cnicBack && !isApproved(documentStatus.cnicBack)) {
      Swal.fire({
        icon: "warning",
        title: "Missing CNIC Back",
        text: "Please attach the back side of your CNIC.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    // 5 MB collective size limit
    const FIVE_MB = 5 * 1024 * 1024;
    const totalBytes = [cv, cnicFront, cnicBack, other]
      .filter(Boolean)
      .reduce((acc, f) => acc + (f?.size || 0), 0);
    if (totalBytes > FIVE_MB) {
      const mb = (totalBytes / (1024 * 1024)).toFixed(2);
      Swal.fire({
        icon: "warning",
        title: "Total Size Exceeded",
        text: `Selected files total ${mb} MB. Maximum allowed is 5 MB.`,
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
      return;
    }

    setLoading(true);
    try {
      const form = new FormData();
      form.append("cv", cv);
      form.append("cnicFront", cnicFront);
      form.append("cnicBack", cnicBack);
      if (other) form.append("other", other);

      const token = tokenService.getToken();
      const base = process.env.REACT_APP_API_URL || "";
      const url = `${base}/documents/upload/${encodeURIComponent(
        jobApplicationId
      )}`;
      await axios.post(url, form, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      Swal.fire({
        icon: "success",
        title: "Documents Uploaded Successfully!",
        text: "Your documents have been submitted and will be reviewed by our team.",
        confirmButtonText: "Go to Dashboard",
      }).then(() => {
        navigate("/emp/dashboard", { replace: true });
      });
      // Generate and show previews for CNIC images post-submission
      const isImage = (file) =>
        !!file &&
        typeof file.type === "string" &&
        file.type.startsWith("image/");
      // Clean old preview URLs if any
      if (cnicFrontPreviewUrl) URL.revokeObjectURL(cnicFrontPreviewUrl);
      if (cnicBackPreviewUrl) URL.revokeObjectURL(cnicBackPreviewUrl);

      const frontUrl = isImage(cnicFront) ? URL.createObjectURL(cnicFront) : "";
      const backUrl = isImage(cnicBack) ? URL.createObjectURL(cnicBack) : "";
      setCnicFrontPreviewUrl(frontUrl);
      setCnicBackPreviewUrl(backUrl);

      // Clear file inputs after successful submission
      setCv(null);
      setCnicFront(null);
      setCnicBack(null);
      setOther(null);
      setTotalSelectedBytes(0);

      // Clear file input values
      document.querySelectorAll('input[type="file"]').forEach((input) => {
        input.value = "";
      });
    } catch (err) {
      const apiError = err?.response?.data?.error;
      const errorCode = apiError?.code || "UPLOAD_ERROR";
      const errorMessage =
        apiError?.message ||
        err?.response?.data?.message ||
        err.message ||
        "Upload failed";
      Swal.fire({
        icon: "error",
        title: errorCode,
        text: errorMessage,
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  // Cleanup preview object URLs on unmount or change
  useEffect(() => {
    return () => {
      if (cnicFrontPreviewUrl) URL.revokeObjectURL(cnicFrontPreviewUrl);
      if (cnicBackPreviewUrl) URL.revokeObjectURL(cnicBackPreviewUrl);
    };
  }, [cnicFrontPreviewUrl, cnicBackPreviewUrl]);

  return (
    <div className="max-w-4xl mx-auto bg-white dark:bg-slate-800 rounded-lg p-5">
      <div className="mb-3">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">
          Upload Documents
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Collective Size Warning Banner */}
        <div className="bg-amber-50 dark:bg-amber-950/20 border-l-4 border-amber-500 p-4 rounded-r-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-amber-600 dark:text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-amber-800 dark:text-amber-200 font-medium">
                Important: The total collective size of all selected documents must not exceed 5 MB. 
                You can review, cancel, or swap individual files below before submitting.
              </p>
            </div>
          </div>
        </div>

        {/* CNIC Section */}
        <div className="bg-gray-50 dark:bg-slate-700/50 p-6 rounded-lg">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
            CNIC Documents
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                CNIC Front Side * <span className="text-xs font-normal text-gray-500 dark:text-gray-400 block sm:inline sm:ml-2">(Formats: JPG, PNG, WEBP, GIF. Max: 5MB)</span>
              </label>
              <input
                type="file"
                ref={cnicFrontInputRef}
                accept="image/*"
                onChange={handleFileChange(setCnicFront, "cnicFront")}
                disabled={isApproved(documentStatus.cnicFront)}
                className={`w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 
                file:rounded-full file:border-0 file:text-sm file:font-semibold 
                ${
                  isApproved(documentStatus.cnicFront)
                    ? "file:bg-gray-300 cursor-not-allowed"
                    : "file:bg-blue-50 hover:file:bg-blue-100"
                }`}
              />
               <p
                className={`mt-2 text-xs ${
                  isApproved(documentStatus.cnicFront)
                    ? "text-green-600"
                    : isRejected(documentStatus.cnicFront)
                    ? "text-red-600"
                    : "text-yellow-600"
                }`}
              >
                Status: {documentStatus.cnicFront}
              </p>
              {cnicFront && (
                <div className="mt-2 flex items-center justify-between bg-blue-50 dark:bg-slate-700 p-2 rounded-lg text-xs">
                  <span className="text-gray-700 dark:text-gray-300">
                    Selected: <span className="font-semibold text-green-600 dark:text-green-400">{cnicFront.name}</span> ({(cnicFront.size / (1024 * 1024)).toFixed(2)} MB)
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setCnicFront(null);
                      recomputeTotalBytes({ cnicFront: null });
                      if (cnicFrontPreviewUrl) {
                        URL.revokeObjectURL(cnicFrontPreviewUrl);
                        setCnicFrontPreviewUrl("");
                      }
                      if (cnicFrontInputRef.current) {
                        cnicFrontInputRef.current.value = "";
                      }
                    }}
                    className="text-red-500 hover:text-red-700 font-semibold px-2 py-1 hover:bg-red-50 dark:hover:bg-red-950/20 rounded transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                CNIC Back Side * <span className="text-xs font-normal text-gray-500 dark:text-gray-400 block sm:inline sm:ml-2">(Formats: JPG, PNG, WEBP, GIF. Max: 5MB)</span>
              </label>
              <input
                type="file"
                ref={cnicBackInputRef}
                accept="image/*"
                onChange={handleFileChange(setCnicBack, "cnicBack")}
                disabled={isApproved(documentStatus.cnicBack)}
                className={`w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 
                file:rounded-full file:border-0 file:text-sm file:font-semibold 
                ${
                  isApproved(documentStatus.cnicBack)
                    ? "file:bg-gray-300 cursor-not-allowed"
                    : "file:bg-blue-50 hover:file:bg-blue-100"
                }`}
               
              />
              <p
                className={`mt-2 text-xs ${
                  isApproved(documentStatus.cnicBack)
                    ? "text-green-600"
                    : isRejected(documentStatus.cnicBack)
                    ? "text-red-600"
                    : "text-yellow-600"
                }`}
              >
                Status: {documentStatus.cnicBack}
              </p>

              {cnicBack && (
                <div className="mt-2 flex items-center justify-between bg-blue-50 dark:bg-slate-700 p-2 rounded-lg text-xs">
                  <span className="text-gray-700 dark:text-gray-300">
                    Selected: <span className="font-semibold text-green-600 dark:text-green-400">{cnicBack.name}</span> ({(cnicBack.size / (1024 * 1024)).toFixed(2)} MB)
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setCnicBack(null);
                      recomputeTotalBytes({ cnicBack: null });
                      if (cnicBackPreviewUrl) {
                        URL.revokeObjectURL(cnicBackPreviewUrl);
                        setCnicBackPreviewUrl("");
                      }
                      if (cnicBackInputRef.current) {
                        cnicBackInputRef.current.value = "";
                      }
                    }}
                    className="text-red-500 hover:text-red-700 font-semibold px-2 py-1 hover:bg-red-50 dark:hover:bg-red-950/20 rounded transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              )}
              
            </div>
          </div>
        </div>

        {/* Other Documents Section */}
        <div className="bg-gray-50 dark:bg-slate-700/50 p-6 rounded-lg">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
            Other Documents
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Curriculum Vitae (CV) * <span className="text-xs font-normal text-gray-500 dark:text-gray-400 block sm:inline sm:ml-2">(Formats: PDF, DOC, DOCX, JPG, PNG. Max: 5MB)</span>
              </label>
              <input
                type="file"
                ref={cvInputRef}
                accept=".pdf,.doc,.docx,.jpeg,.jpg,.png"
                onChange={handleFileChange(setCv, "cv")}
                disabled={isApproved(documentStatus.cv)}
                className={`w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 
                file:rounded-full file:border-0 file:text-sm file:font-semibold 
                ${
                  isApproved(documentStatus.cv)
                    ? "file:bg-gray-300 cursor-not-allowed"
                    : "file:bg-blue-50 hover:file:bg-blue-100"
                }`}
              />
               <p
                className={`mt-2 text-xs ${
                  isApproved(documentStatus.cv)
                    ? "text-green-600"
                    : isRejected(documentStatus.cv)
                    ? "text-red-600"
                    : "text-yellow-600"
                }`}
              >
                Status: {documentStatus.cv}
              </p>
              {cv && (
                <div className="mt-2 flex items-center justify-between bg-blue-50 dark:bg-slate-700 p-2 rounded-lg text-xs">
                  <span className="text-gray-700 dark:text-gray-300">
                    Selected: <span className="font-semibold text-green-600 dark:text-green-400">{cv.name}</span> ({(cv.size / (1024 * 1024)).toFixed(2)} MB)
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setCv(null);
                      recomputeTotalBytes({ cv: null });
                      if (cvInputRef.current) {
                        cvInputRef.current.value = "";
                      }
                    }}
                    className="text-red-500 hover:text-red-700 font-semibold px-2 py-1 hover:bg-red-50 dark:hover:bg-red-950/20 rounded transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Other Documents (Optional) <span className="text-xs font-normal text-gray-500 dark:text-gray-400 block sm:inline sm:ml-2">(Max: 5MB)</span>
              </label>
              <input
                type="file"
                ref={otherInputRef}
                onChange={handleFileChange(setOther, "other")}
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-gray-50 file:text-gray-700 hover:file:bg-gray-100 dark:file:bg-gray-700 dark:file:text-gray-300"
              />
              {other && (
                <div className="mt-2 flex items-center justify-between bg-blue-50 dark:bg-slate-700 p-2 rounded-lg text-xs">
                  <span className="text-gray-700 dark:text-gray-300">
                    Selected: <span className="font-semibold text-green-600 dark:text-green-400">{other.name}</span> ({(other.size / (1024 * 1024)).toFixed(2)} MB)
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setOther(null);
                      recomputeTotalBytes({ other: null });
                      if (otherInputRef.current) {
                        otherInputRef.current.value = "";
                      }
                    }}
                    className="text-red-500 hover:text-red-700 font-semibold px-2 py-1 hover:bg-red-50 dark:hover:bg-red-950/20 rounded transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* File Previews */}
        {(cnicFrontPreviewUrl || cnicBackPreviewUrl) && (
          <div className="bg-gray-50 dark:bg-slate-700/50 p-6 rounded-lg">
            <h3 className="text-md font-semibold text-gray-800 dark:text-gray-100 mb-4">
              Document Previews
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {cnicFrontPreviewUrl ? (
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    CNIC Front Preview
                  </p>
                  <img
                    src={cnicFrontPreviewUrl}
                    alt="CNIC Front Preview"
                    className="w-full max-w-xs h-auto rounded-lg border-2 border-gray-300 dark:border-slate-600 shadow-sm"
                  />
                </div>
              ) : (
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    CNIC Front preview unavailable (file is not an image)
                  </p>
                </div>
              )}
              {cnicBackPreviewUrl ? (
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    CNIC Back Preview
                  </p>
                  <img
                    src={cnicBackPreviewUrl}
                    alt="CNIC Back Preview"
                    className="w-full max-w-xs h-auto rounded-lg border-2 border-gray-300 dark:border-slate-600 shadow-sm"
                  />
                </div>
              ) : (
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    CNIC Back preview unavailable (file is not an image)
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Size Information and Actions */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pt-4 border-t border-gray-200 dark:border-slate-600">
          <div className="space-y-2">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Total selected size:{" "}
              <span className="font-semibold">
                {((totalSelectedBytes || 0) / (1024 * 1024)).toFixed(2)} MB
              </span>{" "}
              / 5.00 MB
            </p>
            <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-3 w-3"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              <span>Allowed formats: .Jpeg, .Jpg, .Png, .Pdf, .Doc, .Docx</span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => {
                setCv(null);
                setCnicFront(null);
                setCnicBack(null);
                setOther(null);
                setTotalSelectedBytes(0);
                if (cnicFrontPreviewUrl) {
                  URL.revokeObjectURL(cnicFrontPreviewUrl);
                  setCnicFrontPreviewUrl("");
                }
                if (cnicBackPreviewUrl) {
                  URL.revokeObjectURL(cnicBackPreviewUrl);
                  setCnicBackPreviewUrl("");
                }
                document
                  .querySelectorAll('input[type="file"]')
                  .forEach((input) => {
                    input.value = "";
                  });
              }}
              className="px-6 py-2 bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-slate-600 transition-colors font-medium"
              disabled={loading}
            >
              Clear All
            </button>
            <button
              type="submit"
              disabled={loading || !jobApplicationId}
              className="px-6 py-2 bg-[#EFA938] text-white rounded-lg hover:bg-[#e09a2a] disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium shadow-md"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg
                    className="animate-spin h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Uploading...
                </span>
              ) : (
                "Submit Documents"
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
